import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Building2, MapPin, Search, Briefcase, SlidersHorizontal, X, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { industryLabel } from "../utils/industryLabel";
import { fetchCountries, fetchRegions } from "../utils/geoApi";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COUNTRIES = [
  { value: "United States", key: "unitedStates" }, { value: "Spain", key: "spain" },
  { value: "Portugal", key: "portugal" }, { value: "Mexico", key: "mexico" },
  { value: "Argentina", key: "argentina" }, { value: "Colombia", key: "colombia" },
  { value: "Brazil", key: "brazil" }, { value: "United Kingdom", key: "unitedKingdom" },
  { value: "France", key: "france" }, { value: "Germany", key: "germany" }, { value: "Canada", key: "canada" },
  { value: "Uruguay", key: "uruguay" },
];
const INDUSTRIES = [
  { value: "Technology", key: "technology" }, { value: "Finance", key: "finance" },
  { value: "Healthcare", key: "healthcare" }, { value: "Marketing", key: "marketing" },
  { value: "Education", key: "education" }, { value: "Legal", key: "legal" },
  { value: "Operations", key: "operations" }, { value: "Engineering", key: "engineering" },
  { value: "Retail", key: "retail" }, { value: "Media", key: "media" }, { value: "Real Estate", key: "realEstate" },
];
const COMPANY_SIZES = [
  { value: "1-10", key: "size_1_10" },
  { value: "11-50", key: "size_11_50" },
  { value: "51-200", key: "size_51_200" },
  { value: "201-500", key: "size_201_500" },
  { value: "501-1000", key: "size_501_1000" },
  { value: "1000+", key: "size_1000_plus" },
];
const SORT_OPTIONS = [
  { value: "most_jobs", key: "sortMostJobs" },
  { value: "newest", key: "sortNewest" },
  { value: "alphabetical", key: "sortAZ" },
];
const EMPTY_FILTERS = {
  keyword: "", country: "", city: "", industry: "",
  size: "", min_jobs: "", sort_by: "most_jobs",
  country_code: "", admin1_name: "",
};
const PAGE_SIZE = 12;

export default function CompaniesPage() {
  const { t } = useLanguage();
  const [companies, setCompanies] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [structuredCountries, setStructuredCountries] = useState([]);
  const [structuredRegions, setStructuredRegions] = useState([]);

  useEffect(() => { fetchCountries().then(setStructuredCountries).catch(() => {}); }, []);
  useEffect(() => {
    if (!filters.country_code) { setStructuredRegions([]); return; }
    fetchRegions(filters.country_code).then(setStructuredRegions).catch(() => setStructuredRegions([]));
  }, [filters.country_code]);

  const QUICK_CHIPS = [
    { key: "tech",        label: t("companies.chipTech"),        industry: "Technology" },
    { key: "hospitality", label: t("companies.chipHospitality"), industry: "Hospitality" },
    { key: "health",      label: t("companies.chipHealth"),      industry: "Healthcare" },
    { key: "finance",      label: t("companies.chipFinance"),     industry: "Finance" },
    { key: "education",   label: t("companies.chipEducation"),   industry: "Education" },
    { key: "remote",      label: t("companies.chipRemote"),      keyword: "remote" },
  ];

  const activeCount = [
    filters.country, filters.city, filters.industry, filters.size, filters.min_jobs,
    filters.country_code, filters.admin1_name,
    filters.sort_by !== "most_jobs" ? filters.sort_by : "",
  ].filter(Boolean).length;

  const fetchCompanies = async (f = filters, p = page) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (f.keyword)   params.set("keyword", f.keyword);
      if (f.country_code) {
        params.set("country_code", f.country_code);
        if (f.admin1_name) params.set("admin1_name", f.admin1_name);
      } else if (f.city) params.set("city", f.city);
      else if (f.country) params.set("country", f.country);
      if (f.industry)  params.set("industry", f.industry);
      if (f.size)      params.set("size", f.size);
      if (f.min_jobs)  params.set("min_jobs", f.min_jobs);
      if (f.sort_by)   params.set("sort_by", f.sort_by);
      params.set("page", p);
      params.set("limit", PAGE_SIZE);
      const res = await axios.get(`${API}/companies?${params}`);
      setCompanies(res.data.companies || []);
      setTotal(res.data.total || 0);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchCompanies(); }, [page]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const goToPage = (p) => {
    const clamped = Math.min(Math.max(1, p), totalPages);
    if (clamped === page) return;
    setPage(clamped);
  };

  const setFilter = (key, val) => setFilters((f) => ({ ...f, [key]: val }));
  const clearAll = () => { const r = EMPTY_FILTERS; setFilters(r); setPage(1); fetchCompanies(r, 1); };
  const handleSearch = (e) => { e?.preventDefault(); setPage(1); fetchCompanies(filters, 1); };
  const handleQuickChip = (chip) => {
    const isActive = chip.industry ? filters.industry === chip.industry : filters.keyword === chip.keyword;
    const newFilters = { ...filters, industry: isActive ? "" : (chip.industry || ""), keyword: chip.keyword !== undefined ? (isActive ? "" : chip.keyword) : filters.keyword };
    setFilters(newFilters);
    setPage(1);
    fetchCompanies(newFilters, 1);
  };

  return (
    <div data-testid="companies-page" className="min-h-screen bg-[#F8FAFC]">

      {/* Header — clean, single-purpose, no repeated explanation */}
      <section className="bg-white border-b border-[#E5E7EB] py-8 px-4 md:px-6 lg:px-8">
        <div className="container mx-auto max-w-5xl">
          <h1 className="font-poppins text-2xl md:text-3xl font-bold text-[#0F172A] mb-1">{t("companies.header")}</h1>
          <p className="text-[#64748B] text-sm mb-5 font-poppins">{t("companies.subtitle")}</p>

          {/* Search bar */}
          <form onSubmit={handleSearch} className="bg-white border border-[#E5E7EB] rounded-2xl shadow-sm p-2 flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1 h-12 border border-[#E5E7EB] rounded-xl focus-within:border-[#028CF8] focus-within:ring-2 focus-within:ring-[#028CF8]/20 transition-all bg-white">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
              <input
                data-testid="companies-search"
                type="text"
                value={filters.keyword}
                onChange={(e) => setFilter("keyword", e.target.value)}
                placeholder={t("companies.searchPlaceholder")}
                className="w-full h-full pl-9 pr-3 text-sm text-slate-900 placeholder-slate-400 bg-transparent focus:outline-none rounded-xl"
              />
            </div>
            <div className="relative h-12 sm:w-48 border border-[#E5E7EB] rounded-xl focus-within:border-[#028CF8] focus-within:ring-2 focus-within:ring-[#028CF8]/20 transition-all bg-white">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
              <input
                type="text"
                value={filters.city}
                onChange={(e) => setFilter("city", e.target.value)}
                placeholder={t("companies.cityPlaceholder")}
                className="w-full h-full pl-9 pr-3 text-sm text-slate-900 placeholder-slate-400 bg-transparent focus:outline-none rounded-xl"
              />
            </div>
            <button type="submit" data-testid="companies-search-button"
              className="h-12 px-6 bg-[#028CF8] hover:bg-[#0270C5] text-white font-semibold rounded-xl text-sm flex items-center gap-2 whitespace-nowrap font-poppins transition-all">
              <Search className="h-4 w-4" /> {t("companies.search")}
            </button>
          </form>

          {/* Quick filter chips — desktop/tablet only; Industry select inside "Filtros" covers this on mobile */}
          <div className="hidden lg:flex items-center gap-2 mt-4 overflow-x-auto" style={{ scrollbarWidth: "none" }} data-testid="companies-quick-chips">
            {QUICK_CHIPS.map((chip) => {
              const isActive = chip.industry ? filters.industry === chip.industry : filters.keyword === chip.keyword;
              return (
                <button
                  key={chip.key}
                  data-testid={`quick-chip-${chip.key}`}
                  onClick={() => handleQuickChip(chip)}
                  style={{
                    padding: "6px 14px", borderRadius: "999px", fontSize: "12px", fontWeight: 600,
                    fontFamily: "Poppins, sans-serif", whiteSpace: "nowrap", flexShrink: 0,
                    cursor: "pointer", transition: "all 0.15s",
                    border: `1.5px solid ${isActive ? "#028CF8" : "#E5E7EB"}`,
                    backgroundColor: isActive ? "#028CF8" : "white",
                    color: isActive ? "white" : "#64748B",
                  }}
                >
                  {chip.label}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 md:px-6 lg:px-8 py-6">

        {/* Filter bar */}
        <div className="flex flex-wrap items-center gap-3 mb-5">
          <button
            onClick={() => setAdvancedOpen(!advancedOpen)}
            data-testid="companies-filter-toggle"
            style={{
              display: "flex", alignItems: "center", gap: "8px",
              height: "40px", padding: "0 16px", borderRadius: "10px",
              border: `1px solid ${advancedOpen || activeCount > 0 ? "#028CF8" : "#E5E7EB"}`,
              background: advancedOpen || activeCount > 0 ? "rgba(2,140,248,0.05)" : "white",
              color: advancedOpen || activeCount > 0 ? "#028CF8" : "#64748B",
              fontSize: "13px", fontWeight: 600, fontFamily: "Poppins, sans-serif",
              cursor: "pointer", transition: "all 0.15s",
            }}
          >
            <SlidersHorizontal style={{ width: 15, height: 15 }} />
            {t("companies.filters")}
            {activeCount > 0 && (
              <span style={{ background: "#028CF8", color: "white", borderRadius: "999px", fontSize: "11px", padding: "1px 7px", fontWeight: 700 }}>
                {activeCount}
              </span>
            )}
            <ChevronDown style={{ width: 14, height: 14, transform: advancedOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
          </button>

          {/* Sort */}
          <select
            value={filters.sort_by}
            onChange={(e) => { setFilter("sort_by", e.target.value); setTimeout(() => fetchCompanies({ ...filters, sort_by: e.target.value }, 1), 0); }}
            style={{ height: "40px", border: "1px solid #E5E7EB", borderRadius: "10px", padding: "0 12px", fontSize: "13px", fontFamily: "Poppins, sans-serif", color: "#0F172A", background: "white", cursor: "pointer", outline: "none" }}
          >
            {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{t(`companies.${o.key}`)}</option>)}
          </select>

          <div className="ml-auto text-sm font-poppins" style={{ color: "#64748B" }} data-testid="companies-count">
            {loading ? t("companies.loading") : (
              <span><strong style={{ color: "#0F172A" }}>{total.toLocaleString()}</strong> {t("companies.found")}</span>
            )}
          </div>

          {activeCount > 0 && (
            <button onClick={clearAll} style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "12px", color: "#94A3B8", background: "none", border: "none", cursor: "pointer", fontFamily: "Poppins, sans-serif" }}>
              <X style={{ width: 12, height: 12 }} /> {t("companies.clearAll")}
            </button>
          )}
        </div>

        {/* Advanced Filters Panel */}
        {advancedOpen && (
          <div
            data-testid="companies-filters-panel"
            style={{
              background: "white", border: "1px solid #E5E7EB", borderRadius: "16px",
              padding: "20px", marginBottom: "24px", boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
            }}
          >
            <p style={{ fontSize: "12px", fontWeight: 700, color: "#028CF8", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "16px", fontFamily: "Poppins, sans-serif" }}>
              {t("companies.filterPanelTitle")}
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: "12px", marginBottom: "12px" }}>
              {/* Country (legacy free-text) */}
              <div>
                <label style={{ display: "block", fontSize: "11px", fontWeight: 600, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "6px", fontFamily: "Poppins, sans-serif" }}>{t("companies.countryLabel")}</label>
                <select value={filters.country} onChange={(e) => setFilter("country", e.target.value)} data-testid="filter-country"
                  style={{ width: "100%", height: "40px", border: "1px solid #E5E7EB", borderRadius: "10px", padding: "0 10px", fontSize: "13px", fontFamily: "Poppins, sans-serif", background: "white", outline: "none", color: filters.country ? "#0F172A" : "#94A3B8" }}>
                  <option value="">{t("companies.allCountries")}</option>
                  {COUNTRIES.map((c) => <option key={c.value} value={c.value}>{t(`companies.countryNames.${c.key}`)}</option>)}
                </select>
              </div>
              {/* Structured region filter (Fase 3) — only for the 5 markets with
                  structured company location; free-text Country/City above still
                  works for every other/legacy company record. */}
              <div>
                <label style={{ display: "block", fontSize: "11px", fontWeight: 600, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "6px", fontFamily: "Poppins, sans-serif" }}>{t("companies.regionLabel")}</label>
                <select value={filters.country_code} onChange={(e) => setFilters((f) => ({ ...f, country_code: e.target.value, admin1_name: "" }))} data-testid="filter-structured-country"
                  style={{ width: "100%", height: "40px", border: "1px solid #E5E7EB", borderRadius: "10px", padding: "0 10px", fontSize: "13px", fontFamily: "Poppins, sans-serif", background: "white", outline: "none", color: filters.country_code ? "#0F172A" : "#94A3B8", marginBottom: "8px" }}>
                  <option value="">{t("companies.allCountries")}</option>
                  {structuredCountries.map((c) => <option key={c.code} value={c.code}>{t(`geo.countryNames.${c.code}`)}</option>)}
                </select>
                {filters.country_code && (
                  <select value={filters.admin1_name} onChange={(e) => setFilter("admin1_name", e.target.value)} data-testid="filter-structured-region"
                    style={{ width: "100%", height: "40px", border: "1px solid #E5E7EB", borderRadius: "10px", padding: "0 10px", fontSize: "13px", fontFamily: "Poppins, sans-serif", background: "white", outline: "none", color: filters.admin1_name ? "#0F172A" : "#94A3B8" }}>
                    <option value="">{t("companies.allRegions")}</option>
                    {structuredRegions.map((r) => <option key={r.code} value={r.name}>{r.name}</option>)}
                  </select>
                )}
              </div>
              {/* Industry */}
              <div>
                <label style={{ display: "block", fontSize: "11px", fontWeight: 600, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "6px", fontFamily: "Poppins, sans-serif" }}>{t("companies.industryLabel")}</label>
                <select value={filters.industry} onChange={(e) => setFilter("industry", e.target.value)} data-testid="industry-filter"
                  style={{ width: "100%", height: "40px", border: "1px solid #E5E7EB", borderRadius: "10px", padding: "0 10px", fontSize: "13px", fontFamily: "Poppins, sans-serif", background: "white", outline: "none", color: filters.industry ? "#0F172A" : "#94A3B8" }}>
                  <option value="">{t("companies.allIndustries")}</option>
                  {INDUSTRIES.map((i) => <option key={i.value} value={i.value}>{t(`companies.industryNames.${i.key}`)}</option>)}
                </select>
              </div>
              {/* Size */}
              <div>
                <label style={{ display: "block", fontSize: "11px", fontWeight: 600, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "6px", fontFamily: "Poppins, sans-serif" }}>{t("companies.sizeLabel")}</label>
                <select value={filters.size} onChange={(e) => setFilter("size", e.target.value)}
                  style={{ width: "100%", height: "40px", border: "1px solid #E5E7EB", borderRadius: "10px", padding: "0 10px", fontSize: "13px", fontFamily: "Poppins, sans-serif", background: "white", outline: "none", color: filters.size ? "#0F172A" : "#94A3B8" }}>
                  <option value="">{t("companies.anySize")}</option>
                  {COMPANY_SIZES.map((s) => <option key={s.value} value={s.value}>{t(`companies.${s.key}`)}</option>)}
                </select>
              </div>
              {/* Min jobs */}
              <div>
                <label style={{ display: "block", fontSize: "11px", fontWeight: 600, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "6px", fontFamily: "Poppins, sans-serif" }}>{t("companies.minJobsLabel")}</label>
                <input type="number" value={filters.min_jobs} onChange={(e) => setFilter("min_jobs", e.target.value)} placeholder={t("companies.minJobsPlaceholder")}
                  style={{ width: "100%", height: "40px", border: "1px solid #E5E7EB", borderRadius: "10px", padding: "0 10px", fontSize: "13px", fontFamily: "Poppins, sans-serif", outline: "none" }} />
              </div>
            </div>

            {/* Actions */}
            <div style={{ display: "flex", gap: "10px", paddingTop: "12px", borderTop: "1px solid #F1F5F9" }}>
              <button
                onClick={() => { setPage(1); fetchCompanies(filters, 1); setAdvancedOpen(false); }}
                style={{ height: "40px", padding: "0 20px", background: "#028CF8", color: "white", borderRadius: "10px", fontSize: "13px", fontWeight: 600, fontFamily: "Poppins, sans-serif", border: "none", cursor: "pointer" }}>
                {t("companies.applyFilters")}
              </button>
              <button onClick={clearAll}
                style={{ height: "40px", padding: "0 20px", background: "white", color: "#64748B", borderRadius: "10px", fontSize: "13px", fontWeight: 600, fontFamily: "Poppins, sans-serif", border: "1px solid #E5E7EB", cursor: "pointer" }}>
                {t("companies.clearAll")}
              </button>
            </div>
          </div>
        )}

        {/* Results */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="bg-white rounded-2xl border border-[#E5E7EB] p-6 shadow-sm animate-pulse">
                <div className="h-14 w-14 bg-slate-100 rounded-2xl mb-4 mx-auto" />
                <div className="h-4 bg-slate-100 rounded w-3/4 mx-auto mb-2" />
                <div className="h-3 bg-slate-100 rounded w-1/2 mx-auto" />
              </div>
            ))}
          </div>
        ) : companies.length === 0 ? (
          <div className="text-center py-20" data-testid="no-companies">
            <Building2 className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <h3 className="font-poppins text-xl font-semibold text-slate-700 mb-2">{t("companies.noResults")}</h3>
            <p className="text-slate-500 text-sm">{t("companies.noResultsDesc")}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="companies-grid">
            {companies.map((company) => (
              <Link
                key={company.company_id}
                to={`/companies/${company.company_id}`}
                data-testid={`company-card-${company.company_id}`}
                className="card-job group block bg-white rounded-2xl border border-[#E5E7EB] p-5 shadow-sm hover:shadow-xl hover:border-[#028CF8]/30 transition-all duration-300"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-12 w-12 flex-shrink-0 rounded-xl bg-[#028CF8]/10 flex items-center justify-center font-poppins font-bold text-[#028CF8] text-xl border border-[#028CF8]/10 group-hover:scale-110 transition-transform overflow-hidden" data-testid="companies-list-avatar">
                    {company.logo_url ? (
                      <img src={company.logo_url} alt={company.name} className="w-full h-full object-cover" />
                    ) : (
                      company.name?.[0]?.toUpperCase()
                    )}
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-poppins font-semibold text-[#0F172A] text-sm leading-snug truncate group-hover:text-[#028CF8] transition-colors">
                      {company.name}
                    </h3>
                    <p className="text-xs text-[#94A3B8] font-poppins truncate">
                      {[industryLabel(company.industry, t), company.location].filter(Boolean).join(" · ")}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-1.5 text-xs font-semibold text-[#028CF8] bg-[#028CF8]/5 rounded-full px-3 py-1.5 w-fit">
                  <Briefcase className="h-3 w-3" />
                  {company.job_count || 0} {t("companies.openRoles")}
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Pagination — only shown when results span more than one page */}
        {!loading && companies.length > 0 && totalPages > 1 && (
          <div className="flex items-center justify-center gap-3 mt-8" data-testid="companies-pagination">
            <button
              data-testid="companies-prev-page"
              aria-label={t("companies.prevPage")}
              disabled={page <= 1}
              onClick={() => goToPage(page - 1)}
              className="h-10 w-10 flex items-center justify-center rounded-xl border border-[#E5E7EB] text-[#64748B] disabled:opacity-40 disabled:cursor-not-allowed hover:border-[#028CF8] hover:text-[#028CF8] transition-all"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-sm font-poppins text-[#64748B] px-1" data-testid="companies-page-indicator">
              {t("companies.pageIndicator").replace("{page}", page).replace("{total}", totalPages)}
            </span>
            <button
              data-testid="companies-next-page"
              aria-label={t("companies.nextPage")}
              disabled={page >= totalPages}
              onClick={() => goToPage(page + 1)}
              className="h-10 w-10 flex items-center justify-center rounded-xl border border-[#E5E7EB] text-[#64748B] disabled:opacity-40 disabled:cursor-not-allowed hover:border-[#028CF8] hover:text-[#028CF8] transition-all"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
